import { useCallback, useEffect } from 'react';
import { SnackbarKey, useSnackbar, VariantType } from 'notistack';

import { MAX_NOTIFICATION_MESSAGE_LENGTH, NOTIFICATION_DISMISS_TIME_MS } from 'constants-module';
import { addNotification, getNotifications, Notification, removeNotification } from 'store';

import { useAppDispatch } from './use-app-dispatch';
import { useAppSelector } from './use-app-selector';

let displayed: any[] = [];

export const useNotifier = () => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getNotifications);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const storeDisplayed = (key?: SnackbarKey) => {
    displayed = [...displayed, key];
  };

  const removeDisplayed = (key: SnackbarKey) => {
    displayed = [...displayed.filter((displayedKey) => displayedKey !== key)];
  };

  useEffect(() => {
    notifications.forEach(({ key, message, options = {}, dismissed = false, autoHideDuration, preventDuplicate }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key);

        return;
      }

      // do nothing if snackbar is already displayed
      if (displayed.includes(key)) return;

      // display snackbar using notistack
      enqueueSnackbar(message, {
        key,
        autoHideDuration,
        preventDuplicate,
        ...options,
        className: 'custom-notification',
        onClose: (event, reason, myKey) => {
          if (options.onClose) {
            options.onClose(event, reason, myKey);
          }
        },
        onExited: (_e, myKey) => {
          // remove this snackbar from redux store
          dispatch(removeNotification(myKey));
          removeDisplayed(myKey);
        },
      });

      // keep track of snackbars that we've displayed
      storeDisplayed(key);
    });
  }, [notifications, closeSnackbar, enqueueSnackbar, dispatch]);
};

export const useNotification = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (message: string, type: VariantType) => {
      dispatch(addNotification({ message, type }));
    },
    [dispatch],
  );
};

export const createNotification = (message: string, variant: VariantType, dismissed = false): Notification => {
  return {
    key: new Date().getTime() + Math.random(),
    dismissed,
    message:
      message.length > MAX_NOTIFICATION_MESSAGE_LENGTH ? message.substr(0, MAX_NOTIFICATION_MESSAGE_LENGTH) : message,
    options: {
      variant,
    },
    autoHideDuration: NOTIFICATION_DISMISS_TIME_MS,
    preventDuplicate: false,
  };
};
